/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Timecode Shift & Sync Calibration Modal (Task 10.5.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import { X, SlidersHorizontal, Check } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useSubtitleStudioStore } from '../../stores/useSubtitleStudioStore';

export const SubtitleShiftModal: React.FC = () => {
  const { t } = useTranslation();
  const {
    isShiftModalOpen,
    activeCueId,
    setShiftModalOpen,
    shiftTimestamps,
  } = useSubtitleStudioStore();

  const [amountSec, setAmountSec] = useState<number>(0.5);
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [scope, setScope] = useState<'all' | 'from_current'>('all');

  if (!isShiftModalOpen) return null;

  const handleApply = () => {
    const finalOffset = direction === 'forward' ? Math.abs(amountSec) : -Math.abs(amountSec);
    shiftTimestamps(finalOffset, scope, activeCueId || undefined);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-150">
      <div className="bg-surface rounded-2xl border border-border shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2">
            <span className="p-2 rounded-lg bg-primary/10 text-primary">
              <SlidersHorizontal className="size-4" />
            </span>
            <h3 className="text-sm font-semibold text-text">
              {t('subtitles.shiftTimeModalTitle')}
            </h3>
          </div>
          <button
            type="button"
            onClick={() => setShiftModalOpen(false)}
            className="p-1 rounded-lg hover:bg-surface-subtle text-text-muted hover:text-text transition-colors"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4 text-xs">
          <p className="text-text-muted">
            {t('subtitles.shiftTimeModalDesc')}
          </p>

          {/* Amount input & presets */}
          <div>
            <label className="font-medium text-text block mb-1.5">
              {t('subtitles.shiftAmount')}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.1"
                min="0.05"
                max="60"
                value={amountSec}
                onChange={(e) => setAmountSec(Math.max(0.01, parseFloat(e.target.value) || 0))}
                className="flex-1 rounded-xl border border-border bg-surface px-3 py-2 text-text font-mono focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
              <div className="flex items-center gap-1">
                {[0.25, 0.5, 1.0, 2.0].map((val) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setAmountSec(val)}
                    className={`px-2 py-1 rounded-lg border text-[11px] font-mono transition-colors ${
                      amountSec === val
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'border-border hover:bg-surface-subtle text-text'
                    }`}
                  >
                    {val}s
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Direction Selector */}
          <div>
            <label className="font-medium text-text block mb-1.5">
              {t('subtitles.shiftDirection')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setDirection('forward')}
                className={`py-2 px-3 rounded-xl border text-center transition-all ${
                  direction === 'forward'
                    ? 'bg-primary/10 border-primary text-primary font-semibold'
                    : 'border-border hover:bg-surface-subtle text-text'
                }`}
              >
                {t('subtitles.shiftForward')}
              </button>
              <button
                type="button"
                onClick={() => setDirection('backward')}
                className={`py-2 px-3 rounded-xl border text-center transition-all ${
                  direction === 'backward'
                    ? 'bg-primary/10 border-primary text-primary font-semibold'
                    : 'border-border hover:bg-surface-subtle text-text'
                }`}
              >
                {t('subtitles.shiftBackward')}
              </button>
            </div>
          </div>

          {/* Scope Selector */}
          <div>
            <label className="font-medium text-text block mb-1.5">
              {t('subtitles.shiftScope')}
            </label>
            <div className="space-y-1.5">
              <label className="flex items-center gap-2.5 p-2 rounded-xl border border-border cursor-pointer hover:bg-surface-subtle">
                <input
                  type="radio"
                  name="scope"
                  checked={scope === 'all'}
                  onChange={() => setScope('all')}
                  className="text-primary focus:ring-primary"
                />
                <span className="text-text">{t('subtitles.shiftAll')}</span>
              </label>

              <label className="flex items-center gap-2.5 p-2 rounded-xl border border-border cursor-pointer hover:bg-surface-subtle">
                <input
                  type="radio"
                  name="scope"
                  checked={scope === 'from_current'}
                  onChange={() => setScope('from_current')}
                  className="text-primary focus:ring-primary"
                />
                <span className="text-text">{t('subtitles.shiftFromCurrent')}</span>
              </label>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-2 p-4 bg-surface-subtle border-t border-border">
          <button
            type="button"
            onClick={() => setShiftModalOpen(false)}
            className="px-4 py-2 text-xs font-medium rounded-xl border border-border hover:bg-surface text-text transition-colors"
          >
            {t('subtitles.cancelBtn')}
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="px-4 py-2 text-xs font-semibold rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-all flex items-center gap-1.5 shadow-sm"
          >
            <Check className="size-4" />
            <span>{t('subtitles.applyShiftBtn')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
